import { default as React, FC, Fragment, useEffect, useState } from 'react';

import { Button, Col, notification, Row } from 'antd';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import apiPaths from 'services/apiPaths';
import { setFooterHeight } from 'src/actions/appActions';
import { UpdateUserPoints } from 'src/actions/authActions';
import BeautyGuideFrame from 'src/components/beautyGuide/BeautyGuideFrame';
import CustomModal from 'src/components/CustomModal';
import { openToast } from 'src/components/general/Toast';
import PointsHowTo from 'src/components/scan/PointsHowTo';
import { IRootReducers } from 'src/reducers';
import { IChallenge } from 'src/shared/models';
import { IBeautyGuideResource } from 'src/shared/models/beautyGuideResource.model';
import {
  completeBeautyGuideResource,
  customFormatMessage,
  isError,
  isProfileCompleted,
  useWindowSize,
} from 'utils';

interface OwnProps {
  config: IBeautyGuideResource;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    user: state.auth.user,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setFooterHeight }, dispatch);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const BeautyGuide: FC<Props> = ({
  challenge,
  user,
  config,
  setShowModal,
  showModal,
  updateChallenge,
  updateUserPoints,
  setFooterHeight,
}) => {
  const { formatMessage } = useIntl();
  const { buttonName } = config;
  const { isLG } = useWindowSize();
  const [triggerEmail, setTriggerEmail] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const showCheckoutButton =
    !showModal && config.isChallengeInDate && !config.isCompleted;

  useEffect(() => {
    if (showCheckoutButton) {
      setFooterHeight('checkout');
    }
    return () => {
      setFooterHeight(null);
    };
  }, [showModal, config.isChallengeInDate, config.isCompleted]);

  useEffect(() => {
    window.addEventListener('message', receiveIframeMessage);
    return () => window.removeEventListener('message', receiveIframeMessage);
  }, []);

  useEffect(() => {
    if (showToast) {
      openNotification(challenge?.challengePoints);
    }
  }, [showToast]);

  const receiveIframeMessage = async (event) => {
    if (!event?.data) {
      // error
      return;
    }
    let data;
    try {
      data = JSON.parse(event?.data);
    } catch {
      return;
    }

    if (data?.type === 'beauty-guide-loaded') {
      setTriggerEmail(true);
    }
    if (data?.type === 'beauty-guide-user-options') {
      await completeResource(data.options);
    }
  };

  const openNotification = (points: number) => {
    const toastMessage = formatMessage(
      { id: 'notification.beauty-guide.success' },
      { points: points, shortDescription: challenge?.descriptionShort }
    );
    openToast(toastMessage, 'success');
  };

  const completeResource = async (data) => {
    try {
      const result = await completeBeautyGuideResource({
        dataPath: apiPaths.CHALLENGES.COMPLETE_CHALLENGE,
        idResource: config.idResource,
        data: { options: JSON.stringify(data) },
        updateChallenge,
        updateUserPoints,
      });

      if (isError(result)) {
        notification.error({
          message: formatMessage({ id: 'app.error.generic' }),
        });
      } else {
        setShowToast(true);
      }
    } catch (err) {
      console.error('Error completing beauty guide:', err);
      notification.error({
        message: formatMessage({ id: 'app.error.generic' }),
      });
    }
  };

  const handleModalExit = () => {
    setShowExitModal(true);
  };

  const handleCloseModalExit = () => {
    setShowExitModal(false);
  };

  const handleCloseModal = () => {
    setShowExitModal(false);
    setShowModal(false);
  };

  const exitBeautyGuideModal = (
    <CustomModal
      {...{
        customClass: 'beauty-guide-modal-exit ',
        visible: showExitModal,
        content: showExitModal ? (
          <>
            <Col className="beauty-guide-modal-exit">
              <h1>{customFormatMessage('beauty-guide.exit-modal.title')}</h1>
              <p>{customFormatMessage('beauty-guide.exit-modal.text')}</p>
              <Button
                className="btn-primary btn-primary--black contest-beauty-guide__button beauty-guide-modal-exit__button"
                onClick={() => handleCloseModalExit()}
              >
                {customFormatMessage('beauty-guide.exit-modal.button')}
              </Button>
              <Button
                className="btn-ghost--minimalist"
                onClick={() => handleCloseModal()}
              >
                <Row align="middle">
                  <span className="beauty-guide-modal-exit__button-text">
                    {customFormatMessage('beauty-guide.exit-modal.exit')}
                  </span>
                </Row>
              </Button>
            </Col>
          </>
        ) : null,
        onCancel: handleCloseModalExit,
        footer: null,
        showIcon: false,
      }}
    />
  );

  const beautyGuideModal = (
    <CustomModal
      {...{
        visible: showModal,
        content: showModal ? (
          <BeautyGuideFrame
            email={user.email}
            triggerEmail={triggerEmail}
            setTriggerEmail={setTriggerEmail}
            className="beauty-guide-frame"
          />
        ) : null,
        onCancel: handleModalExit,
        footer: null,
        showIcon: false,
        customClass: 'beauty-guide-modal full-screen-mobile',
      }}
    />
  );

  const beautyGuideButtonPoints = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage('page.contest.get.points', {
              points: challenge?.challengePoints,
            })}
          </p>
        </div>
      </div>
    );
  };

  const buttonContent = (
    <>
      {beautyGuideButtonPoints()}
      <Button
        className="btn-primary btn-primary--black experience-checkout-button"
        onClick={() => setShowModal(true)}
      >
        <Row justify="center">
          <span>{buttonName}</span>
        </Row>
      </Button>
    </>
  );

  const checkoutButton = (
    <>
      <div className="show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        {buttonContent}
      </div>
      <div className="not_show_on_breakpoint-md experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div>
            <img
              className="experience-checkout-button__container--image"
              src={config.image}
              alt="checkout-img"
            />
            <div className="experience-checkout-button__container--title">
              <span>{challenge?.name}</span>
            </div>
          </div>
          <div>{buttonContent}</div>
        </div>
      </div>
    </>
  );

  const renderPointsHowTo = (
    <>
      {!isProfileCompleted(user?.actionList) && (
        <PointsHowTo
          title="contest.points-how-to.title"
          ctaBlocks={
            isLG
              ? ['complete-profile', 'scan-qr']
              : ['scan-qr', 'complete-profile']
          }
        />
      )}
    </>
  );

  const completedBlock = (
    <div className="contest-beauty-guide__completed">
      <div className="contest-beauty-guide__completed__info">
        <span>
          {customFormatMessage('page.contest.block-completed', {
            points: challenge?.challengePoints,
            challengeName: challenge?.challengeName,
          })}
        </span>
      </div>
      {renderPointsHowTo}
    </div>
  );

  return (
    <>
      {showCheckoutButton && checkoutButton}
      {config.isCompleted && completedBlock}
      {beautyGuideModal}
      {exitBeautyGuideModal}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(BeautyGuide);
